import { z } from "zod";
import type { SimulationComparaisonFormStep } from "./ui-state.js";

const numberLikeSchema = z.union([z.string(), z.number()]).optional();
const consentSchema = z
  .boolean()
  .refine((value) => value, "Le consentement est obligatoire.");

export const simulationComparaisonIntroStepSchema = z.object({});

export const simulationComparaisonBienStepSchema = z
  .object({
    prixAchat: numberLikeSchema,
    loyerAnnuel: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredPositive("prixAchat", input.prixAchat, ctx);
    validateRequiredPositive("loyerAnnuel", input.loyerAnnuel, ctx);
  });

export const simulationComparaisonOptionsStepSchema = z.object({
  slugParameter: z.enum(["resident", "non-resident"], {
    message: "Le profil fiscal est obligatoire.",
  }),
  anneeIndex: numberLikeSchema,
});

export const simulationComparaisonContactStepSchema = z.object({
  email: z.string().email("Email invalide."),
  firstName: z.string().trim().max(120, "Le prenom est trop long."),
  source: z
    .string()
    .trim()
    .min(1, "La source est obligatoire.")
    .max(120, "La source est trop longue."),
  consent: consentSchema,
  honeypot: z.string().max(0, "Requete invalide.").optional(),
});

export const simulationComparaisonFormSchema = z
  .object({
    email: z.string().email("Email invalide."),
    firstName: z.string().trim().max(120, "Le prenom est trop long."),
    source: z
      .string()
      .trim()
      .min(1, "La source est obligatoire.")
      .max(120, "La source est trop longue."),
    consent: consentSchema,
    honeypot: z.string().max(0).optional(),
    prixAchat: numberLikeSchema,
    loyerAnnuel: numberLikeSchema,
    slugParameter: z.enum(["resident", "non-resident"], {
      message: "Le profil fiscal est obligatoire.",
    }),
    anneeIndex: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredPositive("prixAchat", input.prixAchat, ctx);
    validateRequiredPositive("loyerAnnuel", input.loyerAnnuel, ctx);
  });

export const simulationComparaisonStepSchemas: Record<SimulationComparaisonFormStep, z.ZodTypeAny> = {
  intro: simulationComparaisonIntroStepSchema,
  bien: simulationComparaisonBienStepSchema,
  options: simulationComparaisonOptionsStepSchema,
  contact: simulationComparaisonContactStepSchema,
  result: z.object({}),
};

function parseNumberLike(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed =
    typeof value === "number" ? value : Number(value.replaceAll(/\s+/g, ""));
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function validateRequiredPositive(
  fieldName: string,
  value: string | number | undefined,
  ctx: z.RefinementCtx,
): void {
  if (value === undefined || value === null || (typeof value === "string" && !value.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` est obligatoire.`,
    });
    return;
  }
  const parsed = parseNumberLike(value);
  if (parsed === undefined || !Number.isFinite(parsed) || parsed <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` doit etre un nombre positif.`,
    });
  }
}
